import { FIRST_NAMES, LAST_NAMES } from './placeholder-names';

type Random = () => number;

// Reserved by RFC 2606, so mail to a placeholder never reaches a real inbox.
const EMAIL_DOMAINS = ['example.com', 'example.net', 'example.org'];

const hashSeed = (seed: string): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return hash;
};

// mulberry32: Math.random cannot be seeded, and a row must keep its
// placeholders from one render to the next.
const createRandom = (seed: number): Random => {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), state | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// random() is below 1, so the index always lands inside the list.
const pick = (random: Random, items: readonly string[]): string =>
  items[Math.floor(random() * items.length)] as string;

const userName = (random: Random, separator: string): string => {
  const first = pick(random, FIRST_NAMES);
  const last = pick(random, LAST_NAMES);
  const digits = random() < 0.5 ? String(Math.floor(random() * 100)) : '';
  return `${first}${separator}${last}${digits}`.toLowerCase();
};

// A blurred cell is still selectable text, so a placeholder phone must not be
// dialable: lines 0100-0199 of the 555 exchange are reserved for fiction, and
// an area code never repeats its last two digits (911, 800 and 900 are service,
// toll-free and premium codes). National format only, because the table
// rewrites valid numbers to it while the card and popout show the string as is.
const phoneNumber = (random: Random): string => {
  const first = 2 + Math.floor(random() * 8);
  const second = Math.floor(random() * 10);
  const third = (second + 1 + Math.floor(random() * 9)) % 10;
  const line = 100 + Math.floor(random() * 100);
  return `(${first}${second}${third}) 555-0${line}`;
};

export const generateRandomPlaceholder = (
  columnName: string,
  seed: string | number
): string => {
  const random = createRandom(hashSeed(`${seed}-${columnName}`));

  switch (columnName) {
    case 'last_name':
      return pick(random, LAST_NAMES);
    case 'email':
    case 'personal_email':
      return `${userName(random, '.')}@${pick(random, EMAIL_DOMAINS)}`;
    case 'phone_number':
      return phoneNumber(random);
    case 'linkedin_url':
      return `linkedin.com/in/${userName(random, '-')}`;
    default:
      return '';
  }
};
